import classNames from 'classnames';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { getLimitsFromProjectSettings } from '../../routes/TeamManagement/Ceremonies/PMMeeting/DMMeting.helpers';
import { Components } from '../../utils/types';
import {
    BUBBLE_BLUE,
    BUBBLE_GREEN,
    BUBBLE_GREEN_TRANS,
    BUBBLE_PURPLE,
    BUBBLE_RED,
    BUBBLE_RED_TRANS,
    BUBBLE_YELLOW,
    BUBBLE_YELLOW_TRANS,
} from './Bubble.constants';
import { Arc, Bubble, Point } from './Bubbles.types';

const getBubbleColor = (bub: Bubble) => {
    if (bub.end.constSt) return BUBBLE_PURPLE;
    switch (bub.endStatus) {
        case 'green':
            return BUBBLE_GREEN;
        case 'yellow':
            return BUBBLE_YELLOW;
        case 'red':
            return BUBBLE_RED;
    }
};

const getBubbleTransColor = (state: string) => {
    // if (bub.end.constSt) return BUBBLE_PURPLE;
    switch (state) {
        case 'green':
            return BUBBLE_GREEN_TRANS;
        case 'yellow':
            return BUBBLE_YELLOW_TRANS;
        case 'red':
            return BUBBLE_RED_TRANS;
    }
};

const CirclePart = ({ p, comp, fill, hover }: { p: Arc; comp: number; fill: string; hover?: boolean }) => {
    const angle = comp * 3.6;

    // Convert angle to radians for calculations
    const startAngle = 0; // Start angle (0 degrees)
    const endAngle = (angle * Math.PI) / 180; // End angle in radians
    const rad = p.radius * 0.8;

    // Calculate start and end points
    const startX = p.x + rad * Math.cos(startAngle);
    const startY = -p.y + rad * Math.sin(startAngle);
    const endX = p.x + rad * Math.cos(endAngle);
    const endY = -p.y + rad * Math.sin(endAngle);

    // Determine the large arc flag (1 if angle > 180 degrees, 0 otherwise)
    const largeArcFlag = angle > 180 ? 1 : 0;

    return (
        <path
            d={`M${startX},${startY} A ${rad} ${rad} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
            strokeWidth="10"
            fill="none"
            className={classNames(
                'BubbleComp',
                `BubbleComp--color-${fill}`,
                hover && `BubbleComp--color-${fill}--hover`
            )}
            strokeLinecap="round"
            transform={`rotate(270, ${p.x}, ${-p.y})`}
        />
    );
};

export const DrawBubble = ({
    bubble,
    outHover,
    setOutHover,
}: {
    bubble: Bubble;
    outHover?: boolean;
    setOutHover?: Dispatch<SetStateAction<boolean>>;
}) => {
    const [hover, setHover] = useState(false);
    const history = useHistory();
    const match = useRouteMatch();

    const navigate = () => history.push(generatePath(match.path, { wbsElementId: bubble.id }));

    return (
        <g style={outHover && !hover ? { opacity: 0.5 } : {}}>
            <circle
                cx={bubble.start.x}
                cy={-bubble.start.y}
                r={bubble.start.radius}
                className={classNames(
                    'Bubble',
                    `Bubble--color-${bubble.startStatus}`,
                    hover && `Bubble--color-${bubble.startStatus}--hover`
                )}
                onMouseEnter={() => {
                    setHover(true);
                    setOutHover && setOutHover(true);
                }}
                onMouseOut={() => {
                    setHover(false);
                    setOutHover && setOutHover(false);
                }}
                onClick={navigate}
            />

            <circle cx={bubble.start.x} cy={-bubble.start.y} r={5} fill={BUBBLE_BLUE} />

            {bubble.constSt && (
                <circle
                    cx={bubble.end.x}
                    cy={-bubble.end.y}
                    r={16}
                    fill={BUBBLE_PURPLE}
                    onMouseEnter={() => {
                        setHover(true);
                        setOutHover && setOutHover(true);
                    }}
                    onMouseOut={() => {
                        setHover(false);
                        setOutHover && setOutHover(false);
                    }}
                    className="BubbleEnd"
                    onClick={navigate}
                />
            )}
            <line x1={bubble.start.x} y1={-bubble.start.y} x2={bubble.end.x} y2={-bubble.end.y} stroke="black" />

            <circle
                cx={bubble.end.x}
                cy={-bubble.end.y}
                r={5}
                fill={BUBBLE_BLUE}
                onMouseEnter={() => {
                    setHover(true);
                    setOutHover && setOutHover(true);
                }}
                onMouseOut={() => {
                    setHover(false);
                    setOutHover && setOutHover(false);
                }}
                className="BubbleEnd"
                onClick={navigate}
            />

            <circle
                cx={bubble.start.x}
                cy={-bubble.start.y}
                r={13}
                fill="none"
                strokeWidth="6"
                onMouseEnter={() => {
                    setHover(true);
                    setOutHover && setOutHover(true);
                }}
                onMouseOut={() => {
                    setHover(false);
                    setOutHover && setOutHover(false);
                }}
                className="BubbleEnd"
                onClick={navigate}
                stroke={getBubbleTransColor(bubble.qualityStatus)}
            />

            <CirclePart p={bubble.start} comp={bubble.completion} fill={bubble.startStatus} hover={hover} />

            <text x={bubble.start.x - bubble.name.length * 4.1} y={-bubble.start.y + 40}>
                {bubble.name}
            </text>
        </g>
    );
};

export const Axis = ({ windowHeight, windowWidth }: { windowWidth: number; windowHeight: number }) => (
    <>
        <line x1="0" y1="50%" x2="100%" y2="50%" stroke="black" />
        <text x="43%" y="40" className="yAxisText">
            Cost overrun
        </text>
        <path
            d={`M ${windowWidth / 2 - 7} 15 L ${windowWidth / 2 + 7} 15 L ${windowWidth / 2} 0 z`}
            fill={BUBBLE_BLUE}
        />

        <line x1="50%" y1="0" x2="50%" y2="100%" stroke="black" />
        <text x="96%" y="53%">
            Delay
        </text>
        <path
            d={`M ${windowWidth - 15} ${windowHeight / 2 - 7} L ${windowWidth - 15} ${
                windowHeight / 2 + 7
            } L ${windowWidth} ${windowHeight / 2} z`}
            fill={BUBBLE_BLUE}
        />
    </>
);

export const LimitFrame = ({
    projectSetting,
    windowHeight,
    windowWidth,
}: {
    projectSetting: Components.Schemas.ProjectSettingDTO;
    windowWidth: number;
    windowHeight: number;
}) => {
    const limits = getLimitsFromProjectSettings(projectSetting);
    if (
        !limits?.cost?.critalMax ||
        !limits.time.critalMin ||
        !limits.time.critalMax ||
        !limits.cost.critalMin ||
        !limits.cost.cautionMax ||
        !limits.time.cautionMin ||
        !limits.time.cautionMax ||
        !limits.cost.cautionMin
    )
        return <></>;
    return (
        <>
            <rect
                x={limits.time.critalMin * (windowWidth / 100)}
                y={-limits.cost.critalMax * (windowHeight / 100)}
                width={(-limits.time.critalMin + limits.time.critalMax) * (windowWidth / 100)}
                height={-(limits.cost.critalMin - limits.cost.critalMax) * (windowHeight / 100)}
                // rx="16"
                rx="4"
                fill={'none'}
                // fill={BUBBLE_YELLOW_BG}
                strokeDasharray="12 4"
                stroke={BUBBLE_YELLOW_TRANS}
                strokeWidth="2"
            />
            <rect
                x={limits.time.cautionMin * (windowWidth / 100)}
                y={-limits.cost.cautionMax * (windowHeight / 100)}
                width={(-limits.time.cautionMin + limits.time.cautionMax) * (windowWidth / 100)}
                height={-(limits.cost.cautionMin - limits.cost.cautionMax) * (windowHeight / 100)}
                // rx="16"
                rx="4"
                fill={'none'}
                // fill={BUBBLE_GREEN_BR}
                strokeDasharray="12 3"
                stroke={BUBBLE_GREEN_TRANS}
                strokeWidth="2"
            />
            {/* <rect
                x={-windowWidth / 2 + 1}
                y={-windowHeight / 2 + 1}
                width={windowWidth - 2}
                height={windowHeight - 2}
                rx="16"
                fill={'none'}
                strokeDasharray="15 10"
                stroke={BUBBLE_RED_TRANS}
                strokeWidth="2"
            /> */}
        </>
    );
};

export const DrawCross = (point: Point) => {
    return (
        <>
            <line stroke="red" strokeWidth="2" x1={point.x} y1={point.y - 10} x2={point.x} y2={point.y + 10} />
            <line stroke="red" strokeWidth="2" x1={point.x - 10} y1={point.y} x2={point.x + 10} y2={point.y} />
        </>
    );
};
